import axios from "axios";
import { logError, logSuccess } from "@/services/utils/httpUtils";

const apiTamboUrl = axios.create({
  baseURL: process.env.VUE_APP_API_TAMBO || "http://localhost:8000",
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Token desde el primer pedido. `userTamboStore/setupInterceptors` (que corre en
 * `App.mounted`) reemplaza este interceptor por el completo, con refresh; antes de eso,
 * el guard del router y los componentes que piden al crearse salían sin token (401).
 */
export function agregarTokenGuardado(config) {
  const token = localStorage.getItem("access_token");
  if (token && !config.headers?.Authorization) {
    const tipo = localStorage.getItem("token_type") || "Bearer";
    config.headers = config.headers || {};
    config.headers.Authorization = `${tipo} ${token}`;
  }
  const currentTamboId = localStorage.getItem("current_tambo_id");
  if (currentTamboId) {
    config.headers = config.headers || {};
    config.headers["X-Tambo-Id"] = currentTamboId;
  }
  return config;
}

apiTamboUrl.interceptors.request.use(agregarTokenGuardado);

export default apiTamboUrl;

// CRUD
export const apiService = {
  // Shorthands estándar HTTP que delegan a los métodos custom correspondientes
  async get(url, config = {}) {
    return this.getCustom(url, config);
  },

  async post(url, payload, config = {}) {
    return this.postCustom(url, payload, config);
  },

  async patch(url, payload) {
    return this.patchCustom(url, payload);
  },

  async put(url, payload) {
    return this.putCustom(url, payload);
  },

  async getCustom(url, config = {}) {
    try {
      const path = url.startsWith("/") ? url : `/${url}`;
      const response = await apiTamboUrl.get(path, config);
      logSuccess(`GET ${url}`, response.status);
      return response;
    } catch (error) {
      logError(`GET ${url}`, error);
      throw error;
    }
  },
  async getAll(endpointName) {
    try {
      const response = await apiTamboUrl.get(`/${endpointName}/`);
      logSuccess(`GET ${endpointName}`, response.status);
      return response;
    } catch (error) {
      console.error(
        `[DEBUG 405] Failed GET for endpoint: ${endpointName}`,
        error
      );
      logError(`GET ${endpointName}`, error);
      throw error;
    }
  },

  async getById(endpointName, id) {
    try {
      const response = await apiTamboUrl.get(`/${endpointName}/${id}`);
      logSuccess(`GET ${endpointName} por ID ${id}`, response.status);
      return response;
    } catch (error) {
      logError(`GET ${endpointName} por ID ${id}`, error);
      throw error;
    }
  },

  async create(endpointName, payload) {
    try {
      const response = await apiTamboUrl.post(`/${endpointName}/`, payload);
      logSuccess(`POST creando ${endpointName}`, response.status);
      return response;
    } catch (error) {
      logError(`POST creando ${endpointName}`, error);
      throw error;
    }
  },

  async update(endpointName, id, payload) {
    try {
      const response = await apiTamboUrl.patch(
        `/${endpointName}/${id}`,
        payload
      );
      logSuccess(
        `PATCH actualizando ${endpointName} con ID ${id}`,
        response.status
      );
      return response;
    } catch (error) {
      logError(`PATCH actualizando ${endpointName} con ID ${id}`, error);
      throw error;
    }
  },

  async delete(endpointName, id) {
    if (id === undefined) {
      return this.deleteCustom(endpointName);
    }
    try {
      const response = await apiTamboUrl.delete(`/${endpointName}/${id}`);
      logSuccess(
        `DELETE eliminando ${endpointName} con ID ${id}`,
        response.status
      );
      return response;
    } catch (error) {
      logError(`DELETE eliminando ${endpointName} con ID ${id}`, error);
      throw error;
    }
  },

  async uploadFile(endpointName, id, file, fieldName = "file") {
    try {
      const formData = new FormData();
      formData.append(fieldName, file);
      const response = await apiTamboUrl.post(
        `/${endpointName}/${id}/upload-excel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      logSuccess(
        `Archivo subido a ${endpointName} con ID ${id}`,
        response.status
      );
      return response;
    } catch (error) {
      logError(`Error subiendo archivo a ${endpointName} con ID ${id}`, error);
      throw error;
    }
  },

  async postCustom(url, payload, config = {}) {
    try {
      const path = url.startsWith("/") ? url : `/${url}`;
      const response = await apiTamboUrl.post(path, payload, config);
      logSuccess(`POST a ${url}`, response.status);
      return response;
    } catch (error) {
      logError(`Error en el POST a ${url}`, error);
      throw error;
    }
  },

  async patchCustom(url, payload) {
    try {
      const path = url.startsWith("/") ? url : `/${url}`;
      const response = await apiTamboUrl.patch(path, payload);
      logSuccess(`PATCH a ${url}`, response.status);
      return response;
    } catch (error) {
      logError(`Error en el PATCH a ${url}`, error);
      throw error;
    }
  },

  async putCustom(url, payload) {
    try {
      const path = url.startsWith("/") ? url : `/${url}`;
      const response = await apiTamboUrl.put(path, payload);
      logSuccess(`PUT a ${url}`, response.status);
      return response;
    } catch (error) {
      logError(`Error en el PUT a ${url}`, error);
      throw error;
    }
  },

  async deleteCustom(url) {
    try {
      const path = url.startsWith("/") ? url : `/${url}`;
      const response = await apiTamboUrl.delete(path);
      logSuccess(`DELETE a ${url}`, response.status);
      return response;
    } catch (error) {
      logError(`Error en el DELETE a ${url}`, error);
      throw error;
    }
  },
};
